import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { isLocalHost } from '@/utils';

export interface UseDevFallbackToggleResult {
  /** Whether the DEV "force audio fallback" toggle is currently on. */
  forceAudioFallback: boolean;
  setForceAudioFallback: Dispatch<SetStateAction<boolean>>;
  /** Whether the DEV tools (the toggle buttons) should be shown. */
  devToolsVisible: boolean;
  /** Stable mirror of forceAudioFallback for stale-closure-free async reads. */
  forceAudioFallbackRef: MutableRefObject<boolean>;
  /** DEV-only: simulate a 701 ICE timeout by substituting unreachable ICE servers. */
  forceIceTimeout: boolean;
  setForceIceTimeout: Dispatch<SetStateAction<boolean>>;
  /** Stable mirror of forceIceTimeout for stale-closure-free async reads. */
  forceIceTimeoutRef: MutableRefObject<boolean>;
  /** Whether the DEV token readout is currently shown. */
  showTokenUsage: boolean;
  setShowTokenUsage: Dispatch<SetStateAction<boolean>>;
}

/**
 * DEV-only "force audio fallback" toggle. The control is revealed on localhost
 * or when the URL contains ?forceAudioFallback=1 (and pre-enabled when the
 * param is present), letting the audio-only fallback be exercised on networks
 * that don't actually block STUN/TURN. Exposes a ref mirror so async code
 * (startAvatar) can read the latest value without stale closures.
 */
export const useDevFallbackToggle = (): UseDevFallbackToggleResult => {
  const [forceAudioFallback, setForceAudioFallback] = useState<boolean>(false);
  const [forceIceTimeout, setForceIceTimeout] = useState<boolean>(false);
  // Default ON so the readout shows as soon as dev tools are revealed; the
  // toggle lets you hide it.
  const [showTokenUsage, setShowTokenUsage] = useState<boolean>(true);
  const [devToolsVisible, setDevToolsVisible] = useState<boolean>(false);
  const forceAudioFallbackRef = useRef<boolean>(false);
  const forceIceTimeoutRef = useRef<boolean>(false);

  // Reveal the toggle on localhost or when any DEV param is present.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const audioFallbackParam = params.get('forceAudioFallback') === '1';
    const iceTimeoutParam = params.get('forceIceTimeout') === '1';
    // Side-effect-free reveal: shows the DEV tools + token readout without
    // arming any fallback behavior (unlike the two params above).
    const showTokensParam = params.get('showTokens') === '1';
    if (isLocalHost() || audioFallbackParam || iceTimeoutParam || showTokensParam) {
      setDevToolsVisible(true);
    }
    if (audioFallbackParam) {
      setForceAudioFallback(true);
    }
    if (iceTimeoutParam) {
      setForceIceTimeout(true);
    }
    if (showTokensParam) {
      setShowTokenUsage(true);
    }
  }, []);

  // Keep the refs in sync so async startAvatar reads the latest values.
  useEffect(() => {
    forceAudioFallbackRef.current = forceAudioFallback;
  }, [forceAudioFallback]);

  useEffect(() => {
    forceIceTimeoutRef.current = forceIceTimeout;
  }, [forceIceTimeout]);

  return {
    forceAudioFallback,
    setForceAudioFallback,
    devToolsVisible,
    forceAudioFallbackRef,
    forceIceTimeout,
    setForceIceTimeout,
    forceIceTimeoutRef,
    showTokenUsage,
    setShowTokenUsage,
  };
};
